import { AlertTriangle, Loader2 } from 'lucide-react';
import { useLanguage } from '@/i18n';

export const ModuleStateBoundary = ({
  loading,
  hasData,
  emptyState,
  partialIssues = [],
  loadingTestId = 'module-loading-state',
  partialIssuesTestId = 'module-partial-issues',
  children,
}) => {
  const { t } = useLanguage();
  const hasBlockingIssues = partialIssues.some((issue) => issue.required || issue.status === 'error');
  const visibleIssues = partialIssues.slice(0, 4);

  const formatIssueLabel = (issue) => {
    const endpoint = issue?.endpoint || t('moduleState.unknownEndpoint');
    const details = issue?.errorMessage ? ` — ${issue.errorMessage}` : '';

    if (issue?.timedOut) {
      return `${endpoint} — ${t('moduleState.issueTimeout')}${details}`;
    }
    if (issue?.httpStatus) {
      return `${endpoint} — ${t('moduleState.issueHttp', { status: issue.httpStatus })}${details}`;
    }
    if (issue?.required || issue?.status === 'error') {
      return `${endpoint} — ${t('moduleState.issueCritical')}${details}`;
    }
    return `${endpoint} — ${t('moduleState.issueFallback')}${details}`;
  };

  if (loading) {
    return (
      <div className="flex min-h-[220px] items-center justify-center" data-testid={loadingTestId}>
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  if (!hasData && emptyState) {
    return emptyState;
  }

  return (
    <div className="space-y-4" data-testid="module-state-boundary-content">
      {partialIssues.length > 0 && (
        <div
          className={hasBlockingIssues
            ? 'flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-900'
            : 'flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900'}
          data-testid={partialIssuesTestId}
        >
          <AlertTriangle className="mt-0.5 h-5 w-5" />
          <div>
            <p className="font-medium">{hasBlockingIssues ? t('moduleState.blockingTitle') : t('moduleState.partialTitle')}</p>
            <p className="text-sm opacity-80">
              {hasBlockingIssues ? t('moduleState.blockingDescription') : t('moduleState.partialDescription')}
            </p>
            <ul className="mt-3 space-y-1 text-sm" data-testid={`${partialIssuesTestId}-list`}>
              {visibleIssues.map((issue, index) => (
                <li key={`${issue.endpoint}-${index}`} data-testid={`${partialIssuesTestId}-item-${index}`}>
                  • {formatIssueLabel(issue)}
                </li>
              ))}
              {partialIssues.length > visibleIssues.length && (
                <li data-testid={`${partialIssuesTestId}-more`}>
                  • {t('moduleState.moreIssues', { count: partialIssues.length - visibleIssues.length })}
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
      {children}
    </div>
  );
};